import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import ChatPanel from '../../ui/ChatPanel';
import WorkspaceContent from '../../workspace/WorkspaceContent';
import SpinningLoader from '../../ui/SpinningLoader';
import Toast from '../../ui/Toast';
import RenameProjectDialog from '../../modals/RenameProjectDialog';
import { Project, ConversationEntry } from '../../../types/project';
import { ChatMessage } from '../../../types/chat';
import { streamingAPI, StreamingQueryResponse } from '../../../services/streamingApi';
import { BackendErrorParser } from '../../../services/errorParser';
import { parseErrorFromString } from '../../ErrorDisplay';
import { ParsedError } from '../../../types/errors';

interface DashboardWorkspaceViewProps {
  project: Project;
  initialQuestion?: string;
  initialAttachments?: File[];
  onRenameProject?: (project: Project, newName: string) => Promise<void>;
}


const DashboardWorkspaceView: React.FC<DashboardWorkspaceViewProps> = ({
  project,
  initialQuestion = '',
  initialAttachments = [],
  onRenameProject
}) => {
  const location = useLocation();
  const [currentProject, setCurrentProject] = useState<Project>(project);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showWorkspace, setShowWorkspace] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isChatLoading, setIsChatLoading] = useState(false); // New state for chat panel loading only
  const [streamingResponse, setStreamingResponse] = useState<StreamingQueryResponse | undefined>();
  const [latestStreamResult, setLatestStreamResult] = useState<StreamingQueryResponse | undefined>();
  const [isLoadingConversation, setIsLoadingConversation] = useState(true);
  const [conversations, setConversations] = useState<ConversationEntry[]>([]);
  const [initialConversations, setInitialConversations] = useState<ConversationEntry[]>([]);
  const [isCurrentQueryFollowUp, setIsCurrentQueryFollowUp] = useState(false);
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [queryEntryId, setQueryEntryId] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error' | 'info' | 'warning'>('error');
  const [isUploadingFiles, setIsUploadingFiles] = useState(false); // Track file upload state
  const [uploadingMessageId, setUploadingMessageId] = useState<string | null>(null); // Track which message is uploading
  const [uploadProgress, setUploadProgress] = useState<number>(0); // Track upload progress percentage
  const [hasUserApproved, setHasUserApproved] = useState(false); // Track if user has approved execution plan
  const [isSyncing, setIsSyncing] = useState(false); // Track if sync is in progress
  const [syncProgress, setSyncProgress] = useState<number>(0); // Track sync progress percentage separately
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false); // Track rename dialog state
  const hasInitialized = useRef(false);
  const currentEventSource = useRef<EventSource | null>(null);
  const currentStreamingResponse = useRef<StreamingQueryResponse | undefined>();
  // Guard to prevent multiple fetchQueryFullResult calls
  const fullResultFetchedRef = useRef(false);

  // Sync currentProject with project prop
  useEffect(() => {
    setCurrentProject(project);
  }, [project]);

  // Helper function to prevent duplicate messages from being added
  const addMessageWithoutDuplicates = (newMessage: ChatMessage | ChatMessage[]) => {
    setMessages(prev => {
      const messagesToAdd = Array.isArray(newMessage) ? newMessage : [newMessage];
      const updatedMessages = [...prev];

      messagesToAdd.forEach(msg => {
        // Check if message with same ID already exists
        const existingIndex = updatedMessages.findIndex(m => m.id === msg.id);

        if (existingIndex === -1) {
          // Message doesn't exist, add it
          updatedMessages.push(msg);
        } else {
          // Message exists by ID, update it if content has changed
          if (updatedMessages[existingIndex].content !== msg.content ||
            JSON.stringify(updatedMessages[existingIndex].metadata) !== JSON.stringify(msg.metadata)) {
            updatedMessages[existingIndex] = msg;
          }
        }
      });

      return updatedMessages;
    });
  };

  // Helper function to merge streaming responses, preserving existing data
  const mergeStreamingResponse = (current: StreamingQueryResponse | undefined, newData: StreamingQueryResponse): StreamingQueryResponse => {
    if (!current) return newData;

    const merged = { ...current, ...newData };

    // Merge areas at root level (new structure)
    if (current.areas && newData.areas) {
      merged.areas = {
        ...current.areas,
        ...newData.areas
      };
    } else if (newData.areas) {
      merged.areas = newData.areas;
    }

    // Merge structured_response if both exist
    if (current.structured_response && newData.structured_response) {
      merged.structured_response = {
        ...current.structured_response,
        ...newData.structured_response
      };

      // Merge areas specifically in structured_response (legacy structure)
      if (current.structured_response.areas && newData.structured_response.areas) {
        merged.structured_response.areas = {
          ...current.structured_response.areas,
          ...newData.structured_response.areas
        };
      } else if (newData.structured_response.areas) {
        merged.structured_response.areas = newData.structured_response.areas;
      }

      // Merge arrays by combining unique items
      if (current.structured_response.recommendations && newData.structured_response.recommendations) {
        merged.structured_response.recommendations = [
          ...current.structured_response.recommendations,
          ...newData.structured_response.recommendations.filter(
            newRec => !current.structured_response!.recommendations!.includes(newRec)
          )
        ];
      }


      if (current.structured_response.next_steps && newData.structured_response.next_steps) {
        merged.structured_response.next_steps = [
          ...current.structured_response.next_steps,
          ...newData.structured_response.next_steps.filter(
            newStep => !current.structured_response!.next_steps!.includes(newStep)
          )
        ];
      }
    }

    // Merge steps array, keeping all unique steps
    if (current.steps && newData.steps) {
      const existingStepIds = new Set(current.steps.map(step => step.stepId));
      const newSteps = newData.steps.filter(step => !existingStepIds.has(step.stepId));
      merged.steps = [...current.steps, ...newSteps];
    }

    return merged;
  };

  // Initialize conversations from project - SIMPLIFIED VERSION
  useEffect(() => {
    console.log('📦 Initializing workspace with project:', currentProject.project_id);
    console.log('📦 Conversation history:', currentProject.conversation_history);

    if (currentProject.conversation_history && currentProject.conversation_history.length > 0) {
      setConversations(currentProject.conversation_history);

      // Convert and set messages
      const convertedMessages = convertConversationToMessages(currentProject.conversation_history);
      console.log('📦 Converted messages:', convertedMessages);
      addMessageWithoutDuplicates(convertedMessages);

      // Get the last entry with a structured_response (research mode result)
      const lastResearchEntry = currentProject.conversation_history
        .slice()
        .reverse()
        .find(entry => entry.result && (entry.result as any).structured_response);

      console.log('📦 Last research entry:', lastResearchEntry);

      // If we have a research result, display it in the workspace
      if (lastResearchEntry && lastResearchEntry.result) {
        console.log('📦 Setting workspace to show research result');
        setLatestStreamResult(lastResearchEntry.result as any);
        setStreamingResponse(lastResearchEntry.result as any);
        setShowWorkspace(true);
        console.log('📦 Showing workspace (has structured_response)');
      } else {
        console.log('📦 No research results to display in workspace');
      }

      setIsLoadingConversation(false);
    } else {
      console.log('📦 No conversation history, showing empty workspace');
      setConversations([]);
      setIsLoadingConversation(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- convertConversationToMessages/addMessageWithoutDuplicates are recreated each render; effect must only re-run when the conversation history changes
  }, [currentProject.conversation_history]);

  // Update messages when conversations change
  useEffect(() => {
    if (conversations.length > 0) {
      const convertedMessages = convertConversationToMessages(conversations);
      addMessageWithoutDuplicates(convertedMessages);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- convertConversationToMessages/addMessageWithoutDuplicates are recreated each render; effect must only re-run when conversations change
  }, [conversations]);

  // Convert ConversationEntry to ChatMessage for display
  const convertConversationToMessages = (entries: ConversationEntry[]): ChatMessage[] => {
    const messages: ChatMessage[] = [];

    // Check if project has files and add them as the first file message if they exist
    if (currentProject.files && currentProject.files.length > 0) {
      // Group files by upload time or add them all in one message
      const fileMessage: ChatMessage = {
        id: 'project-files',
        role: 'file',
        content: '',
        timestamp: currentProject.files[0].uploaded_at || currentProject.created_at,
        metadata: {
          files: currentProject.files.map(file => ({
            name: file.filename,  // Changed from file.name
            size: file.file_size,  // Changed from file.size
            type: file.content_type,  // Changed from file.type
            uploaded_at: file.uploaded_at
          }))
        }
      };
      messages.push(fileMessage);
    }

    entries.forEach(entry => {
      // Always add the original query/result messages first
      // Add user message
      messages.push({
        id: entry.query.query_id,
        role: 'user',
        content: entry.query.query,
        timestamp: entry.timestamp
      });

      // Check if this is a follow-up query (chat mode) with an answer in the result
      if (entry.is_followup && entry.result && entry.result.answer) {
        console.log('📝 Adding follow-up answer from history for query:', entry.query_id);
        messages.push({
          id: `answer-${entry.entry_id}`,
          role: 'assistant',
          content: entry.result.answer,
          timestamp: entry.result.timestamp || entry.timestamp
        });
        // Skip processing chat_messages for follow-up queries since we've already added the answer
        return;
      }

      // Additionally, add any chat_messages if they exist
      if (entry.chat_messages && entry.chat_messages.length > 0) {
        entry.chat_messages.forEach((chatMsg, index) => {
          // Skip if this is the same as the original query message
          if (chatMsg.content === entry.query.query && chatMsg.message_type === 'user') {
            return; // Skip duplicate
          }

          // Map message_type to role
          let role: 'user' | 'assistant' | 'system' | 'file' = 'user';
          if (chatMsg.message_type === 'user') {
            role = 'user';
          } else if (chatMsg.message_type === 'system' || chatMsg.message_type === 'assistant') {
            role = 'assistant';
          } else if (chatMsg.message_type === 'processing' || chatMsg.message_type === 'execution_plan') {
            role = 'system';
          }

          // Check if this is a file message based on metadata
          if (chatMsg.metadata?.files) {
            messages.push({
              id: `${entry.entry_id}-chat-${index}`,
              role: 'file',
              content: '',
              timestamp: chatMsg.timestamp || new Date().toISOString(),
              metadata: chatMsg.metadata
            });
          } else if (chatMsg.message_type === 'execution_plan' && chatMsg?.metadata?.executionPlan) {
            messages.push({
              id: `plan-${Date.now()}`,
              content: 'EXECUTION_PLAN',
              role: 'assistant',
              timestamp: chatMsg.timestamp || new Date().toISOString(),
              metadata: {
                type: 'execution_plan',
                executionPlan: typeof chatMsg?.metadata?.executionPlan === 'string'
                  ? JSON.parse(chatMsg?.metadata?.executionPlan)
                  : chatMsg?.metadata?.executionPlan,
                queryId: chatMsg?.metadata?.queryId
              }
            });

          } else {
            messages.push({
              id: `${entry.entry_id}-chat-${index}`,
              role: role,
              content: chatMsg.content,
              timestamp: chatMsg.timestamp || new Date().toISOString(),
              metadata: chatMsg.metadata
            });
          }
        });
      }
    });

    return messages;
  };


  // Define handleSendMessage with useCallback to avoid dependency issues
  const handleSendMessage = useCallback(async (message: string, files: File[], metadata?: any) => {
    // Create array of messages to add
    const newMessages: ChatMessage[] = [];

    let queryId: string;
    let eventSource: EventSource | null;

    // If there are files, add file message FIRST (before user message)
    if (files.length > 0) {
      const fileMessage: ChatMessage = {
        id: `${Date.now()}-files`,
        content: '',
        role: 'file',
        timestamp: new Date().toISOString(),
        metadata: {
          files: files.map(f => ({
            name: f.name,
            size: f.size,
            type: f.type,
            uploaded_at: new Date().toISOString()
          }))
        }
      };
      newMessages.push(fileMessage);
    }

    // Add user message AFTER file message
    // Use a temporary ID that will be updated with the actual query ID from backend
    const tempUserId = `temp-user-${Date.now()}`;
    const userMessage: ChatMessage = {
      id: tempUserId,
      content: message,
      role: 'user',
      timestamp: new Date().toISOString(),
      metadata: metadata  // Include metadata if provided
    };

    if (!metadata?.hasConversation) {
      newMessages.push(userMessage);
    }
    addMessageWithoutDuplicates(newMessages);
    setAttachedFiles([]);

    // Set uploading state if there are files
    if (files.length > 0) {
      setIsUploadingFiles(true);
      setUploadingMessageId(userMessage.id);
      setUploadProgress(0); // Reset progress to 0
    }


    // Check if we're currently in a pending_approval state
    const lastMessage = messages[messages.length - 1];
    const isRespondingToExecutionPlan = lastMessage?.metadata?.type === 'execution_plan';
    const currentQueryId = lastMessage?.metadata?.queryId || streamingResponse?.query_id || '';

    // Check if this is an approval from the Proceed button (has special metadata)
    const isButtonApproval = userMessage.metadata?.source === 'approval_button';

    // If there's a pending execution plan and user sends a new message (not button approval)
    // we need to stop the current stream and start a new one
    if (isRespondingToExecutionPlan && currentQueryId && streamingResponse?.status === 'pending_approval' && !isButtonApproval) {
      // Close the existing event source if it's still open
      if (currentEventSource.current) {
        currentEventSource.current.close();
        currentEventSource.current = null;
      }

      // Clear the streaming response and reset streaming state
      setStreamingResponse(undefined);
      setIsStreaming(false);
      setHasUserApproved(false); // Reset approval state for new query
      currentStreamingResponse.current = undefined;
      eventSource = null;
    }

    // Only process approval if it's from the button (has special metadata)
    if (isButtonApproval && isRespondingToExecutionPlan && streamingResponse?.status === 'pending_approval') {

      // Use the queryId from the button metadata for accuracy
      const approvalQueryId = userMessage.metadata?.queryId || currentQueryId;

      if (approvalQueryId) {
        try {
          await streamingAPI.submitApprovalFeedback(approvalQueryId, message);
          // Set approval state and show workspace when user approves/proceeds
          setHasUserApproved(true);
          setShowWorkspace(true);
        } catch (error) {
          console.error('Failed to submit approval feedback:', error);

          // Add error message to chat instead of navigating away
          const errorMessage: ChatMessage = {
            id: `error-${Date.now()}`,
            content: `Failed to approve execution plan: ${error instanceof Error ? error.message : 'Unknown error'}. Please try again.`,
            role: 'system',
            timestamp: new Date().toISOString(),
            metadata: { type: 'error' }
          };
          addMessageWithoutDuplicates(errorMessage);
        }
        return;
      } else {
        console.error('No queryId found for approval');

        // Add error message to chat
        const errorMessage: ChatMessage = {
          id: `error-${Date.now()}`,
          content: 'No query ID found for approval. Please try submitting your query again.',
          role: 'system',
          timestamp: new Date().toISOString(),
          metadata: { type: 'error' }
        };
        addMessageWithoutDuplicates(errorMessage);
        return;
      }
    }

    // Check if there's at least one conversation with results (for follow-up queries)
    // IMPORTANT: Exclude rejected queries - they should not trigger follow-up mode
    const hasConversationWithResults = conversations.some(conv =>
      conv.result !== null &&
      conv.result !== undefined &&
      conv.result.status !== 'rejected'
    );

    // Find the latest query_id from conversations with results (for follow-up)
    let latestQueryId: string | undefined;
    let parentEntryId: string | undefined = queryEntryId;

    if (hasConversationWithResults) {
      // Find the latest conversation entry with results (excluding rejected queries)
      const conversationsWithResults = conversations.filter(conv =>
        conv.result !== null &&
        conv.result !== undefined &&
        conv.result.status !== 'rejected'
      );
      if (conversationsWithResults.length > 0) {
        const latestConversation = conversationsWithResults[conversationsWithResults.length - 1];
        latestQueryId = latestConversation.query_id;
        parentEntryId = queryEntryId || latestConversation.entry_id;
      }
    }

    try {
      // If there is at least one conversation with results, use follow-up endpoints
      // Check both conversations state AND initialConversations to handle all cases
      // IMPORTANT: Exclude rejected queries - they should not trigger follow-up mode
      const hasValidInitialResult = initialConversations.length > 0 &&
        initialConversations[0].result &&
        initialConversations[0].result.status !== 'rejected';

      if ((hasConversationWithResults && latestQueryId) || hasValidInitialResult) {
        // Mark this as a follow-up query
        setIsCurrentQueryFollowUp(true);

        // Set chat loading to true to show loading dots in chat panel only
        setIsChatLoading(true);

        const queryIdValue = latestQueryId || initialConversations[0].query_id;

        // Use follow-up query endpoints (conversational, no streaming)
        let result: { queryId: string; answer: string; status: string; failedFiles?: string[]; };
        if (files && files.length > 0) {
          try {
            result = await streamingAPI.submitFollowupQueryWithFiles(
              currentProject.project_id,
              queryIdValue,
              message,
              files,
              true, // includeProjectFiles
              {}, // additionalContext
              (progress) => setUploadProgress(progress) // Progress callback
            );

            // Check if any files failed to upload
            if (result.failedFiles && result.failedFiles.length > 0) {
              const failedFilesList = result.failedFiles.join(', ');
              setToastMessage(`Failed to upload: ${failedFilesList}. Query will continue with successfully uploaded files.`);
              setToastType('warning');
              setToastVisible(true);
            }
          } finally {
            // Clear upload state after upload completes (success or failure)
            setIsUploadingFiles(false);
            setUploadingMessageId(null);
            setUploadProgress(0); // Reset progress
          }
        } else {
          result = await streamingAPI.submitFollowupQuery(
            queryIdValue,
            message,
            true, // includeProjectFiles
            {} // additionalContext
          );
        }
        queryId = result.queryId;

        // Update the temporary user message ID with the actual query ID from backend
        setMessages(prev => prev.map(msg =>
          msg.id === tempUserId ? { ...msg, id: queryId } : msg
        ));

        // Stop showing loading dots
        setIsChatLoading(false);

        // Save the follow-up user message to backend
        if (parentEntryId) {
          streamingAPI.addChatMessage(currentProject.project_id, {
            entry_id: parentEntryId,
            message_type: 'user',
            content: message,
            query_id: queryId,
            metadata: {
              is_followup: true,
              files: files.length > 0 ? files.map(f => ({ name: f.name, size: f.size, type: f.type })) : undefined
            }
          }).catch(error => {
            console.error('Failed to save follow-up user message:', error);
          });
        }

        // For follow-up queries, display the answer directly (no streaming)
        if (result.answer) {
          const messageId = `response-${queryId}`;

          // Check if message with this ID already exists and add if not
          const existingMessage = messages.find(msg => msg.id === messageId);

          if (!existingMessage) {
            // Add new message
            const responseMessage: ChatMessage = {
              id: messageId,
              content: result.answer,
              role: 'assistant',
              timestamp: new Date().toISOString()
            };
            setMessages(prev => {
              // Double-check inside setState to prevent race conditions
              const alreadyExists = prev.find(msg => msg.id === messageId);
              if (alreadyExists) {
                return prev;
              }
              return [...prev, responseMessage];
            });

            // NOTE: Do NOT save follow-up response to chat_messages here
            // The backend already stores the answer in the conversation entry's result field
            // Saving it to chat_messages would create a duplicate when the conversation is reloaded
          }
        }


        return; // Exit early for follow-up queries
      } else {
        // Mark this as not a follow-up query
        setIsCurrentQueryFollowUp(false);
        // Use normal submitProjectQuery endpoints for initial queries
        // Determine mode: use project mode if set, otherwise default to research (true)
        const isAgentMode = currentProject.mode === 'chat' ? false : true;

        let result;
        if (files && files.length > 0) {
          try {
            result = await streamingAPI.submitProjectQueryWithFiles(
              currentProject.project_id,
              message,
              files,
              parentEntryId,
              true, // includeProjectFiles
              {}, // additionalContext
              isAgentMode, // agentMode
              (progress: number) => setUploadProgress(progress) // onUploadProgress callback
            );

            // Check if any files failed to upload
            if (result.failedFiles && result.failedFiles.length > 0) {
              const failedFilesList = result.failedFiles.join(', ');
              setToastMessage(`Failed to upload: ${failedFilesList}. Query will continue with successfully uploaded files.`);
              setToastType('warning');
              setToastVisible(true);
            }
          } finally {
            // Clear upload state after upload completes (success or failure)
            setIsUploadingFiles(false);
            setUploadingMessageId(null);
            setUploadProgress(0); // Reset progress
          }
        } else {
          result = await streamingAPI.submitProjectQuery(
            currentProject.project_id,
            message,
            parentEntryId,
            true, // includeProjectFiles
            {}, // additionalContext
            isAgentMode // agentMode
          );
        }

        queryId = result.queryId;
        eventSource = result.eventSource;
        parentEntryId = result.entryId;
        setQueryEntryId(result.entryId);

        // Update the temporary user message ID with the actual query ID from backend
        setMessages(prev => prev.map(msg =>
          msg.id === tempUserId ? { ...msg, id: queryId } : msg
        ));

        if (!eventSource) return;
        // Only set streaming state after successful submission (including file upload if applicable)
        console.log('[DashboardWorkspaceView] Setting isStreaming to TRUE');
        setIsStreaming(true);
        // Initialize streaming response with default values for initial queries
        if (!(hasConversationWithResults && latestQueryId)) {
          const initialStreamingResponse: StreamingQueryResponse = {
            status: 'processing',
            currentStep: 'Initializing...',
            currentAgent: undefined,
            engagedAgents: [],
            completedAgents: [],
            agentStatuses: [],
            progressPercentage: 0,
            progress: 0,
            timestamp: new Date().toISOString()
          };
          setStreamingResponse(initialStreamingResponse);
          currentStreamingResponse.current = initialStreamingResponse;
        }

        currentEventSource.current = eventSource;

        // Set up event listeners for streaming updates
        eventSource.addEventListener('update', async (event) => {
          const data: StreamingQueryResponse = JSON.parse(event.data);

          // Check if this is a completion event (done: true)
          if ((data.done === true || (data.status === 'completed' && data.progress === 100)) && !fullResultFetchedRef.current) {
            console.log('Query completed, fetching full result...', data);

            try {
              // Prevent multiple fetches
              fullResultFetchedRef.current = true;

              // Fetch the full result from the new endpoint
              const fullResult = await streamingAPI.fetchQueryFullResult(queryId);

              // Handle failed queries to prevent infinite loops
              if (fullResult.status === 'failed') {
                console.log('Skipping failed query in update event:', queryId);
                return;
              }

              // Use the full result instead of merging streamed data
              const finalData = { ...fullResult, done: true };

              setStreamingResponse(finalData);
              setLatestStreamResult(finalData);

              if (!initialConversations.length) {
                // Create initial conversation from finalData for new projects
                const conversationEntry: ConversationEntry = {
                  entry_id: `entry-${Date.now()}`,
                  project_id: currentProject.project_id,
                  timestamp: new Date().toISOString(),
                  is_followup: false,
                  parent_entry_id: null,
                  query: {
                    query_id: finalData.query_id || finalData.queryId || queryId,
                    query: message,
                    context: {
                      project_id: currentProject.project_id,
                      is_followup: false,
                      query_type: "new_analysis",
                      parent_entry_id: null,
                      include_project_files: true,
                      require_approval: false,
                      run_full_analysis: true
                    },
                    focus_areas: [],
                    timeframe: null,
                    user_id: null,
                    session_id: currentProject.project_id,
                    debug: false
                  },
                  query_id: finalData.query_id || finalData.queryId || queryId,
                  result: finalData.structured_response ? {
                    query_id: finalData.query_id || finalData.queryId || queryId,
                    status: "completed",
                    summary: finalData.result || null,
                    sections: {
                      executive_summary: finalData.structured_response.executive_summary || "",
                      areas: finalData.areas || finalData.structured_response.areas || {},
                      recommendations: finalData.structured_response.recommendations || [],
                      next_steps: finalData.structured_response.next_steps || [],
                      agent_responses: finalData.structured_response.agent_responses || {},
                      metrics: finalData.structured_response.metrics || [],
                      key_insights: finalData.structured_response.key_insights || null,
                      swot_analysis: finalData.structured_response.swot_analysis || {
                        strengths: [],
                        weaknesses: [],
                        opportunities: [],
                        threats: []
                      }
                    },
                    visualizations: finalData.visualizations || [],
                    sources: finalData.sources || [],
                    confidence_score: finalData.confidence_score || null,
                    execution_time_ms: finalData.execution_time_ms || null,
                    errors: finalData.errors || []
                  } : null
                };

                setInitialConversations([conversationEntry]);
              }

              // Add chat message based on mode
              if (finalData.structured_response?.executive_summary) {
                // Research mode: Add executive summary as chat message
                const summaryMessage: ChatMessage = {
                  id: `summary-${queryId}`,
                  content: finalData.structured_response.executive_summary,
                  role: 'assistant',
                  timestamp: new Date().toISOString(),
                  metadata: {
                    is_result: true,
                    queryId: queryId
                  }
                };
                addMessageWithoutDuplicates(summaryMessage);
              } else if (finalData.result || finalData.final_response) {
                // Chat mode: Add result or final_response as chat message
                const chatResponse = finalData.result || finalData.final_response;
                const chatMessage: ChatMessage = {
                  id: `chat-${queryId}`,
                  content: chatResponse,
                  role: 'assistant',
                  timestamp: new Date().toISOString(),
                  metadata: {
                    is_result: true,
                    queryId: queryId
                  }
                };
                console.log('[CHAT MODE] Adding chat message:', chatMessage);
                addMessageWithoutDuplicates(chatMessage);
              }

              // Update conversations state to enable follow-up query detection
              // Create a conversation entry from the completed query
              const newConversationEntry: ConversationEntry = {
                entry_id: `entry-${queryId}`,
                project_id: currentProject.project_id,
                timestamp: finalData.timestamp || new Date().toISOString(),
                query: {
                  query_id: queryId,
                  query: finalData.query || '',
                  context: {},
                  focus_areas: [],
                  timeframe: null,
                  user_id: null,
                  session_id: currentProject.project_id,
                  debug: false
                },
                query_id: queryId,
                result: finalData as any, // Store the full result
                is_followup: false,
                parent_entry_id: null
              };

              // Add to conversations state
              setConversations(prev => [...prev, newConversationEntry]);
              console.log('[CONVERSATION] Added new conversation entry for follow-up detection:', newConversationEntry);

              setIsStreaming(false);
              // Only show workspace if there's a structured_response (research mode)
              if (finalData.structured_response) {
                setShowWorkspace(true);
              }
            } catch (error) {
              console.error('Error fetching full result:', error);
              // Fallback to merged data if fetch fails
              const mergedFinalData = currentStreamingResponse.current ?
                mergeStreamingResponse(currentStreamingResponse.current, { ...data, done: true }) :
                { ...data, done: true };

              setStreamingResponse(mergedFinalData);
              setLatestStreamResult(mergedFinalData);

              if (mergedFinalData.structured_response?.executive_summary) {
                const summaryMessage: ChatMessage = {
                  id: `summary-${Date.now()}`,
                  content: mergedFinalData.structured_response.executive_summary,
                  role: 'assistant',
                  timestamp: new Date().toISOString()
                };
                addMessageWithoutDuplicates(summaryMessage);
              }

              setIsStreaming(false);
              // Only show workspace if there's a structured_response (research mode)
              if (mergedFinalData.structured_response) {
                setShowWorkspace(true);
              }
            } finally {
              eventSource?.close();
              currentEventSource.current = null;
            }
            return;
          }

          // Check if status is rejected
          if (data.status === 'rejected') {
            // Set streamingResponse status to rejected
            setStreamingResponse(prev => ({ ...prev, status: 'rejected' } as StreamingQueryResponse));

            // Close the stream
            eventSource?.close();
            currentEventSource.current = null;
            const content = data?.rejection_reason || data?.result || "Oh, that's a curveball! I don't have the answer for that, but I can definitely help you with any business strategy questions you have."

            // Add rejection message to chat
            const rejectionMessage: ChatMessage = {
              id: `rejection-${Date.now()}`,
              content,
              role: 'assistant',
              timestamp: new Date().toISOString()
            };

            addMessageWithoutDuplicates(rejectionMessage);
            setIsStreaming(false);
            setIsChatLoading(false); // Reset chat loading state to allow new queries
            setHasUserApproved(false); // Reset approval on rejection

            // Save rejection message to backend
            if (parentEntryId) {
              streamingAPI.addChatMessage(currentProject.project_id, {
                entry_id: parentEntryId,
                message_type: 'system',
                content: content,
                query_id: queryId,
                metadata: { rejection: true }
              }).catch(error => {
                console.error('Failed to save rejection message:', error);
              });
            }
            return;
          }

          // Check if we just entered pending_approval status
          if (data.status === 'pending_approval' && data.executionPlan) {
            const planMessageId = `plan-${data.queryId}`;

            // Check if execution plan message already exists
            const existingPlan = messages.find(msg => msg.id === planMessageId);

            if (!existingPlan) {
              // Create an AI message with the execution plan
              const planMessage: ChatMessage = {
                id: planMessageId,
                content: 'EXECUTION_PLAN',
                role: 'assistant',
                timestamp: new Date().toISOString(),
                metadata: {
                  type: 'execution_plan',
                  executionPlan: data.executionPlan,
                  queryId: data.queryId
                }
              };

              setMessages(prev => {
                // Double-check inside setState to prevent race conditions
                const alreadyExists = prev.find(msg => msg.id === planMessageId);
                if (alreadyExists) {
                  return prev;
                }

                // Message will be added, so schedule the API call
                // Use setTimeout to ensure setState has completed
                setTimeout(() => {
                  if (parentEntryId) {
                    streamingAPI.addChatMessage(currentProject.project_id, {
                      entry_id: parentEntryId as string,
                      message_type: 'execution_plan',
                      content: 'EXECUTION_PLAN',
                      query_id: queryId,
                      metadata: {
                        type: 'execution_plan',
                        executionPlan: JSON.stringify(data.executionPlan),
                        queryId: data.queryId
                      }
                    }).catch(error => {
                      console.error('Failed to save execution plan:', error);
                    });
                  }
                }, 1000);

                return [...prev, planMessage];
              });
            }
          }

          const mergedData = mergeStreamingResponse(currentStreamingResponse.current, data);
          currentStreamingResponse.current = mergedData;
          setStreamingResponse(mergedData);
        });

        // Listen for detailed progress events from the backend
        eventSource.addEventListener('progress', (event) => {
          try {
            const progressData: StreamingQueryResponse = JSON.parse(event.data);
            console.log('[PROGRESS EVENT] Received:', {
              status: progressData.status,
              currentStep: progressData.currentStep,
              currentAgent: progressData.currentAgent,
              progressPercentage: progressData.progressPercentage,
              engagedAgents: progressData.engagedAgents,
              completedAgents: progressData.completedAgents
            });

            // Update streaming response with progress information
            const updatedResponse: StreamingQueryResponse = {
              ...currentStreamingResponse.current,
              status: progressData.status,
              currentStep: progressData.currentStep,
              currentAgent: progressData.currentAgent,
              engagedAgents: progressData.engagedAgents,
              completedAgents: progressData.completedAgents,
              agentStatuses: progressData.agentStatuses,
              progressPercentage: progressData.progressPercentage,
              progress: progressData.progressPercentage, // Also set progress for backward compatibility
              timestamp: progressData.timestamp
            };

            currentStreamingResponse.current = updatedResponse;
            setStreamingResponse(updatedResponse);
            console.log('[PROGRESS EVENT] Updated streamingResponse state');

            // Check if query is completed
            if (progressData.status === 'completed' && progressData.progressPercentage === 100) {
              console.log('[PROGRESS EVENT] ✅ Query completed! Status=completed, Progress=100%');
              // Only show workspace if there's a structured_response (research mode)
              if (progressData.structured_response) {
                console.log('[PROGRESS EVENT] Setting showWorkspace=true (has structured_response)');
                setShowWorkspace(true);
              } else {
                console.log('[PROGRESS EVENT] NOT showing workspace (no structured_response - chat mode)');
              }
              setIsStreaming(false);
            }

            // Log agent progress for debugging
            if (progressData.currentAgent) {
              console.log(`[AGENT PROGRESS] ${progressData.currentAgent} - ${progressData.currentStep} (${progressData.progressPercentage}%)`);
            }
            if (progressData.engagedAgents && progressData.engagedAgents.length > 0) {
              console.log(`[ENGAGED AGENTS] ${progressData.engagedAgents.join(', ')}`);
            }
            if (progressData.completedAgents && progressData.completedAgents.length > 0) {
              console.log(`[COMPLETED AGENTS] ${progressData.completedAgents.join(', ')}`);
            }
          } catch (error) {
            console.error('[PROGRESS EVENT ERROR]', error);
          }
        });

        eventSource.addEventListener('done', async (event) => {
          const rawFinalData: StreamingQueryResponse = JSON.parse(event.data);

          try {
            // Fetch the full result from the new endpoint
            const fullResult = await streamingAPI.fetchQueryFullResult(queryId);

            // Handle failed queries to prevent infinite loops
            if (fullResult.status === 'failed') {
              console.log('Skipping failed query in main done event:', queryId);
              return;
            }

            // Use the full result instead of merging streamed data
            const finalData = { ...fullResult, done: true };

            setStreamingResponse(finalData);
            setLatestStreamResult(finalData);

            if (!initialConversations.length) {
              // Create initial conversation from finalData for new projects
              const conversationEntry: ConversationEntry = {
                entry_id: `entry-${Date.now()}`,
                project_id: currentProject.project_id,
                timestamp: new Date().toISOString(),
                query: {
                  query_id: finalData.query_id || finalData.queryId || queryId,
                  query: message,
                  context: {
                    project_id: currentProject.project_id,
                    is_followup: false,
                    query_type: "new_analysis",
                    parent_entry_id: null,
                    include_project_files: true,
                    require_approval: false,
                    run_full_analysis: true
                  },
                  focus_areas: [],
                  timeframe: null,
                  user_id: null,
                  session_id: currentProject.project_id,
                  debug: false
                },
                query_id: finalData.query_id || finalData.queryId || queryId,
                result: finalData.structured_response ? {
                  query_id: finalData.query_id || finalData.queryId || queryId,
                  status: "completed",
                  summary: finalData.result || null,
                  sections: {
                    executive_summary: finalData.structured_response.executive_summary || "",
                    areas: finalData.areas || finalData.structured_response.areas || {},
                    recommendations: finalData.structured_response.recommendations || [],
                    next_steps: finalData.structured_response.next_steps || [],
                    agent_responses: finalData.structured_response.agent_responses || {},
                    metrics: finalData.structured_response.metrics || [],
                    key_insights: finalData.structured_response.key_insights || null,
                    swot_analysis: finalData.structured_response.swot_analysis || {
                      strengths: [],
                      weaknesses: [],
                      opportunities: [],
                      threats: []
                    }
                  },
                  visualizations: finalData.visualizations || [],
                  sources: finalData.sources || [],
                  confidence_score: finalData.confidence_score ?? null,
                  execution_time_ms: finalData.execution_time_ms ?? null,
                  errors: finalData.errors || []
                } : null,
                is_followup: false,
                parent_entry_id: null
              };

              // Also update initial conversations for consistency
              setInitialConversations([conversationEntry]);
            }

            // Add executive summary as chat message
            if (finalData.structured_response?.executive_summary) {
              const summaryMessage: ChatMessage = {
                id: `summary-${queryId}`,
                content: finalData.structured_response.executive_summary,
                role: 'assistant',
                timestamp: new Date().toISOString(),
                metadata: {
                  is_result: true,
                  queryId: queryId
                }
              };
              addMessageWithoutDuplicates(summaryMessage);
            }

            console.log('[SSE] Query completed, setting isStreaming=false');
            setIsStreaming(false);
            // Only show workspace if there's a structured_response (research mode)
            if (finalData.structured_response) {
              console.log('[SSE] Showing workspace (has structured_response)');
              setShowWorkspace(true);
            } else {
              console.log('[SSE] NOT showing workspace (no structured_response - chat mode)');
            }
          } catch (error) {
            console.error('Error fetching full result:', error);
            // Fallback to merged data if fetch fails
            const mergedFinalData = currentStreamingResponse.current ?
              mergeStreamingResponse(currentStreamingResponse.current, { ...rawFinalData, done: true }) :
              { ...rawFinalData, done: true };

            setStreamingResponse(mergedFinalData);
            setLatestStreamResult(mergedFinalData);

            // Add executive summary as chat message
            if (mergedFinalData.structured_response?.executive_summary) {
              const summaryMessage: ChatMessage = {
                id: `summary-${queryId}`,
                content: mergedFinalData.structured_response.executive_summary,
                role: 'assistant',
                timestamp: new Date().toISOString(),
                metadata: {
                  is_result: true,
                  queryId: queryId
                }
              };
              addMessageWithoutDuplicates(summaryMessage);
            }

            setIsStreaming(false);
            // Only show workspace if there's a structured_response (research mode)
            if (mergedFinalData.structured_response) {
              setShowWorkspace(true);
            }
          } finally {
            eventSource?.close();
            currentEventSource.current = null;
          }
        });

        eventSource.addEventListener('error', (event) => {
          console.error('Streaming error:', event);

          // // Show error toast
          // setToastMessage('An error occurred while processing your query. Please try sending your message again.');
          // setToastType('error');
          // setToastVisible(true);

          // Add a helpful message in the chat
          // const errorMessage: ChatMessage = {
          //   id: (Date.now() + 2).toString(),
          //   content: 'Something went wrong while processing your request. Please try sending your message again.',
          //   role: 'assistant',
          //   timestamp: new Date().toISOString()
          // };
          // setMessages(prev => [...prev, errorMessage]);

          setIsStreaming(false);
          setIsChatLoading(false);
          eventSource?.close();
          currentEventSource.current = null;
        });
      }

    } catch (error) {
      console.error('Error submitting query:', error);

      // Reset both loading states
      setIsChatLoading(false);
      setIsStreaming(false);

      // Parse the error using our unified error parser
      let parsedError: ParsedError | null = null;
      let errorContent = 'Failed to submit query. Please try again.';
      let toastContent = 'Failed to submit your query. Please try again.';

      if (error instanceof Error) {
        // Try to parse structured error from JSON string
        parsedError = parseErrorFromString(error.message);

        if (parsedError) {
          // Use structured error information
          errorContent = parsedError.userMessage;
          toastContent = parsedError.userMessage;

          // Add suggestions for validation errors
          if (parsedError.type === 'validation') {
            const suggestions = BackendErrorParser.getValidationSuggestions(parsedError.code);
            if (suggestions.length > 0) {
              errorContent += '\n\n**Try asking about:**\n' + suggestions.map(s => `• ${s}`).join('\n');
            }
          }
        } else {
          // Fallback to legacy error handling
          if (error.message.includes('Failed to upload files')) {
            errorContent = 'Failed to upload files. Please check the file format and size, then try again.';
            toastContent = 'Failed to upload files. Check file format and size.';
          } else if (error.message.includes('422')) {
            errorContent = 'The uploaded files could not be processed. Please ensure they are in a supported format.';
            toastContent = 'Files could not be processed. Check the format.';
          }
        }
      }

      // Show error toast
      setToastMessage(toastContent);
      setToastType('error');
      setToastVisible(true);

      // Create error message with retry option
      const errorMessage: ChatMessage = {
        id: (Date.now() + 3).toString(),
        content: errorContent,
        role: 'assistant',
        timestamp: new Date().toISOString(),
        metadata: {
          isError: true,
          errorType: parsedError?.type || 'unknown',
          originalQuery: parsedError?.originalQuery || message,
          canRetry: parsedError?.retryable || false
        }
      };

      addMessageWithoutDuplicates(errorMessage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- helper functions (addMessageWithoutDuplicates, mergeStreamingResponse, etc.) are recreated each render; including them would recreate this callback every render and defeat the memoization
  }, [project, setMessages, setAttachedFiles, setIsStreaming, setStreamingResponse, setLatestStreamResult, setShowWorkspace, streamingResponse]);

  // Handle sync project - re-run the initial query
  const handleSyncProject = useCallback(async () => {
    // Get the query to sync - try multiple sources in priority order:
    // 1. Latest stream result query
    // 2. Project's initial_query field
    // 3. First conversation entry's query
    const firstConversationQuery = currentProject.conversation_history?.[0]?.query?.query;
    const queryToSync = latestStreamResult?.query || currentProject.initial_query || firstConversationQuery;

    if (!queryToSync) {
      console.warn('No initial query to sync - checked initial_query and conversation history');
      return;
    }

    // Check if already processing
    if (isStreaming) {
      console.warn('A query is already being processed');
      return;
    }

    const startTime = new Date().toISOString();

    try {
      setIsSyncing(true);
      setSyncProgress(0);
      setShowWorkspace(true);
      currentStreamingResponse.current = undefined;

      const uploadedFiles = attachedFiles.map(file => ({
        file_id: file.name,
        filename: file.name,
        file_type: file.type || 'document'
      }));

      const result = await streamingAPI.submitSyncQuery(
        currentProject.project_id,
        queryToSync,
        currentProject.name || 'Untitled Project',
        uploadedFiles
      );

      if (result.eventSource) {
        currentEventSource.current = result.eventSource;

        // Set up event listeners for streaming updates with execution plan handling
        result.eventSource.addEventListener('update', async (event) => {
          try {
            const data: StreamingQueryResponse = JSON.parse(event.data);
            console.log('Sync update received:', data);

            // Update sync progress if available (only increase, never decrease)
            // Check both 'progress' and 'progressPercentage' fields for compatibility
            const progressValue = data.progressPercentage ?? data.progress;
            if (progressValue !== undefined && progressValue !== null) {
              setSyncProgress(prevProgress => {
                const newProgress = Math.max(prevProgress, progressValue);
                console.log(`[SYNC UPDATE] Progress updated: ${prevProgress}% -> ${newProgress}%`);
                return newProgress;
              });
            }

            // Check if this is a completion event (done: true or status is completed)
            if (data.done === true || data.status === 'completed') {
              console.log('Sync query completed, fetching full result...', data);

              // Set progress to 100% immediately on completion
              setSyncProgress(100);

              try {
                // Get the query ID from the current data
                const currentQueryId = data.query_id || data.queryId;

                if (!currentQueryId) {
                  console.error('No query ID found in completion data, reloading page');
                  window.location.reload();
                  return;
                }

                // Fetch the full result from the new endpoint
                const fullResult = await streamingAPI.fetchQueryFullResult(currentQueryId);

                // Handle failed queries to prevent infinite loops
                if (fullResult.status === 'failed') {
                  console.log('Skipping failed query in sync update:', currentQueryId);
                  setIsSyncing(false);
                  setSyncProgress(0);
                  return;
                }

                // Use the full result instead of merging streamed data
                const finalData = { ...fullResult, done: true, startTime, endTime: new Date().toISOString() };

                setStreamingResponse(finalData);
                setLatestStreamResult(finalData);
                setIsSyncing(false); // Reset syncing state on completion
                setSyncProgress(0); // Reset sync progress
                // Only show workspace if there's a structured_response (research mode)
                if (finalData.structured_response) {
                  setShowWorkspace(true);
                }
                setIsStreaming(false); // Mark streaming as complete

              } catch (error) {
                console.error('Error fetching full result:', error);
                // Fallback to merged data if fetch fails
                const mergedFinalData = currentStreamingResponse.current ?
                  mergeStreamingResponse(currentStreamingResponse.current, { ...data, done: true }) :
                  { ...data, done: true };

                setStreamingResponse(mergedFinalData);
                setLatestStreamResult(mergedFinalData);

                if (mergedFinalData.structured_response?.executive_summary) {
                  const summaryMessage: ChatMessage = {
                    id: `summary-${Date.now()}`,
                    content: mergedFinalData.structured_response.executive_summary,
                    role: 'assistant',
                    timestamp: new Date().toISOString()
                  };
                  addMessageWithoutDuplicates(summaryMessage);
                }

                setIsStreaming(false);
                setIsSyncing(false); // Reset syncing state on error
                setSyncProgress(0); // Reset sync progress
                // Only show workspace if there's a structured_response (research mode)
                if (mergedFinalData.structured_response) {
                  setShowWorkspace(true);
                }
              } finally {
                result.eventSource?.close();
                currentEventSource.current = null;
              }
              return;
            }

            // Check if status is rejected
            if (data.status === 'rejected') {
              // Set streamingResponse status to rejected
              setStreamingResponse(prev => ({ ...prev, status: 'rejected' } as StreamingQueryResponse));

              setIsSyncing(false); // Reset syncing state on rejection
              setSyncProgress(0); // Reset sync progress

              const content = data?.rejection_reason || data?.result || "Oh, that's a curveball! I don't have the answer for that, but I can definitely help you with any business strategy questions you have."

              // Add rejection message to chat
              const rejectionMessage: ChatMessage = {
                id: `rejection-sync-${Date.now()}`,
                content,
                role: 'assistant',
                timestamp: new Date().toISOString()
              };

              addMessageWithoutDuplicates(rejectionMessage);
              setIsStreaming(false);
              setIsChatLoading(false);

              // Close the stream
              result.eventSource?.close();
              currentEventSource.current = null;

              return;
            }

          } catch (error) {
            console.error('Error parsing SSE data:', error);
          }
        });

        // Listen for detailed progress events from the backend (sync-specific)
        result.eventSource.addEventListener('progress', (event) => {
          try {
            const progressData: StreamingQueryResponse = JSON.parse(event.data);
            console.log('[SYNC PROGRESS EVENT] Received:', {
              status: progressData.status,
              currentStep: progressData.currentStep,
              currentAgent: progressData.currentAgent,
              progressPercentage: progressData.progressPercentage,
              engagedAgents: progressData.engagedAgents,
              completedAgents: progressData.completedAgents
            });

            // Update sync progress (only increase, never decrease) using progressPercentage from backend
            if (progressData.progressPercentage !== undefined && progressData.progressPercentage !== null) {
              setSyncProgress(prevProgress => {
                const newProgress = Math.max(prevProgress, progressData.progressPercentage || 0);
                console.log(`[SYNC PROGRESS] Updated: ${prevProgress}% -> ${newProgress}%`);
                return newProgress;
              });
            }

            // Handle completion in progress event (backup for update event)
            if (progressData.status === 'completed') {
              console.log('[SYNC PROGRESS] Completed status received, setting progress to 100%');
              setSyncProgress(100);
            }

            // Log agent progress for debugging
            if (progressData.currentAgent) {
              console.log(`[SYNC AGENT PROGRESS] ${progressData.currentAgent} - ${progressData.currentStep} (${progressData.progressPercentage}%)`);
            }
          } catch (error) {
            console.error('[SYNC PROGRESS EVENT ERROR]', error);
          }
        });

        result.eventSource.addEventListener('done', async (event) => {
          const rawFinalData: StreamingQueryResponse = JSON.parse(event.data);
          console.log('Sync done received:', rawFinalData);

          // Set progress to 100% immediately on completion
          setSyncProgress(100);

          try {
            if (!latestStreamResult?.queryId) {
              // Add page re-load here
              window.location.reload();
            }
            else {
              // Fetch the full result from the new endpoint
              const fullResult = await streamingAPI.fetchQueryFullResult(latestStreamResult?.queryId);

              // Handle failed queries to prevent infinite loops
              if (fullResult.status === 'failed') {
                console.log('Skipping failed query in sync done:', latestStreamResult?.queryId);
                setIsSyncing(false);
                setSyncProgress(0);
                return;
              }

              // Use the full result instead of merging streamed data
              const finalData = { ...fullResult, done: true, startTime, endTime: new Date().toISOString() };

              setStreamingResponse(finalData);
              setLatestStreamResult(finalData);
              setIsSyncing(false); // Reset syncing state on completion
              setSyncProgress(0); // Reset sync progress
              // Only show workspace if there's a structured_response (research mode)
              if (finalData.structured_response) {
                setShowWorkspace(true);
              }
            }
          } catch (error) {
            console.error('Error fetching full result:', error);
            // Fallback to merged data if fetch fails
            const mergedFinalData = currentStreamingResponse.current ?
              mergeStreamingResponse(currentStreamingResponse.current, { ...rawFinalData, done: true }) :
              { ...rawFinalData, done: true };

            setStreamingResponse(mergedFinalData);
            setLatestStreamResult(mergedFinalData);

            if (mergedFinalData.structured_response?.executive_summary) {
              const summaryMessage: ChatMessage = {
                id: `summary-${Date.now()}`,
                content: mergedFinalData.structured_response.executive_summary,
                role: 'assistant',
                timestamp: new Date().toISOString()
              };
              addMessageWithoutDuplicates([summaryMessage]);
            }

            setIsStreaming(false);
            setIsSyncing(false); // Reset syncing state on error
            setSyncProgress(0); // Reset sync progress
            // Only show workspace if there's a structured_response (research mode)
            if (mergedFinalData.structured_response) {
              setShowWorkspace(true);
            }
          } finally {
            result.eventSource?.close();
            currentEventSource.current = null;
          }
        });

        result.eventSource.onerror = (error: Event) => {
          const eventSource = error.target as EventSource;

          // readyState: 0 = CONNECTING, 1 = OPEN, 2 = CLOSED
          switch (eventSource.readyState) {
            case EventSource.CONNECTING:
              console.error('[SSE] Failed to connect - setting isStreaming to FALSE');
              console.error('URL:', eventSource.url);
              setIsStreaming(false);
              setIsChatLoading(false);
              setIsSyncing(false); // Reset syncing state on connection error
              setSyncProgress(0); // Reset sync progress
              break;

            case EventSource.OPEN:
              console.error('[SSE] Error while connected - setting isStreaming to FALSE:', error);
              setIsStreaming(false);
              setIsChatLoading(false);
              setIsSyncing(false); // Reset syncing state on error
              setSyncProgress(0); // Reset sync progress
              break;

            case EventSource.CLOSED:
              // Check if we already received the done signal
              if (currentStreamingResponse.current?.done) {
                // This is expected - connection closed after successful completion
                console.log('[SSE] Connection closed after completion (expected)');
              } else {
                // This is an actual error - connection closed before completion
                console.error('[SSE] Connection closed unexpectedly - setting isStreaming to FALSE');
                setIsStreaming(false);
                setIsChatLoading(false);
                setIsSyncing(false); // Reset syncing state on unexpected close
                setSyncProgress(0); // Reset sync progress
              }
              break;
          }

          // Clean up
          currentEventSource.current = null;
          if (eventSource.readyState !== EventSource.CLOSED) {
            eventSource.close();
          }
        };
      }
    } catch (error) {
      console.error('Failed to sync project:', error);
      setIsStreaming(false);
      setIsChatLoading(false);
      setIsSyncing(false); // Reset syncing state on error
      setSyncProgress(0); // Reset sync progress
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- helper functions are recreated each render and streamingAPI is a stable module import; including all reported deps would recreate this callback every render
  }, [currentProject.initial_query, currentProject.conversation_history, currentProject.name, currentProject.project_id, currentProject.files, isStreaming, setIsStreaming, setMessages, setStreamingResponse, setLatestStreamResult, setShowWorkspace, mergeStreamingResponse, streamingAPI, latestStreamResult, attachedFiles]);

  // Handle initial question and attachments from navigation state - SIMPLIFIED
  useEffect(() => {
    if (!currentProject || hasInitialized.current) return;

    const loadConversationData = async () => {
      const state = location.state as any;

      // If there's an initial question (from new project creation), submit it
      if (state?.initialQuestion && state?.autoSubmitQuery && !hasInitialized.current) {
        console.log('🚀 Auto-submitting initial question:', state.initialQuestion);
        setIsLoadingConversation(false);
        hasInitialized.current = true;

        if (state?.initialEntryId) {
          setQueryEntryId(state.initialEntryId);
        }

        // Submit the question
        setTimeout(() => {
          handleSendMessage(state.initialQuestion, state.initialAttachments || [], {
            hasConversation: currentProject.conversation_history && currentProject.conversation_history.length > 0
          });
          window.history.replaceState({}, document.title);
        }, 100);
      } else {
        // For existing projects, just mark as initialized
        // The conversation history is already loaded by the first useEffect
        console.log('📦 Existing project, already initialized');
        setIsLoadingConversation(false);
        hasInitialized.current = true;
      }
    };

    loadConversationData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional run-once initialization guarded by hasInitialized ref; must not re-run when handleSendMessage/currentProject identities change
  }, [location.state, project]);


  const allowedFileTypes = ['.pdf', '.doc', '.docx', '.ppt', '.pptx', '.csv', '.xls', '.xlsx', '.json'];
  const allowedMimeTypes = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'text/csv',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/json'
  ];

  const isValidFile = (file: File) => {
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    return allowedFileTypes.includes(fileExtension) || allowedMimeTypes.includes(file.type);
  };

  const addFiles = (files: File[]) => {
    const validFiles = files.filter(isValidFile);
    const newFiles = validFiles.filter(file =>
      !attachedFiles.some(existingFile =>
        existingFile.name === file.name &&
        existingFile.size === file.size &&
        existingFile.lastModified === file.lastModified
      )
    );

    if (newFiles.length > 0) {
      setAttachedFiles(prev => [...prev, ...newFiles]);
    }

    const invalidCount = files.length - validFiles.length;
    const duplicateCount = validFiles.length - newFiles.length;

    if (invalidCount > 0 || duplicateCount > 0) {
      let message = '';
      if (invalidCount > 0) {
        message += `${invalidCount} file(s) were not added. Only PDF, DOC, DOCX, PPT, PPTX, CSV, XLS, XLSX, and JSON files are allowed.`;
      }
      if (duplicateCount > 0) {
        if (message) message += ' ';
        message += `${duplicateCount} duplicate file(s) were skipped.`;
      }
      console.warn(message);
    }
  };


  const handleFileSelect = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = '.pdf,.doc,.docx,.ppt,.pptx,.csv,.xls,.xlsx,.json';

    input.onchange = (e) => {
      const files = Array.from((e.target as HTMLInputElement).files || []);
      addFiles(files);
    };

    input.click();
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    addFiles(files);
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const items = Array.from(e.clipboardData.items);
    const files = items
      .filter(item => item.kind === 'file')
      .map(item => item.getAsFile())
      .filter((file): file is File => file !== null);

    if (files.length > 0) {
      addFiles(files);
    }
  };

  const removeFile = (index: number) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Rename project handlers
  const handleRenameConfirm = async (newName: string) => {
    if (!onRenameProject) return;

    try {
      await onRenameProject(currentProject, newName);
      setCurrentProject(prev => ({ ...prev, name: newName }));
      setToastMessage('Project has been renamed successfully!');
      setToastType('success');
      setToastVisible(true);
      setIsRenameDialogOpen(false);
    } catch (error) {
      setToastMessage('Renaming project was unsuccessful due to a technical issue.');
      setToastType('error');
      setToastVisible(true);
    }
  };

  const handleRenameCancel = () => {
    setIsRenameDialogOpen(false);
  };


  // Clean up event source on unmount
  useEffect(() => {
    return () => {
      if (currentEventSource.current) {
        currentEventSource.current.close();
      }
    };
  }, []);


  if (isLoadingConversation) {
    return (
      <main className="dashboard-workspace-view">
        <div className="projects-loading">
          <SpinningLoader size="large" text="Loading conversation..." />
        </div>
      </main>
    );
  }

  return (
    <main className="dashboard-workspace-view">
      {/* Left Chat Panel */}
      <ChatPanel
        key={project.project_id} // Force re-render when project changes
        messages={messages}
        onSendMessage={handleSendMessage}
        onFileSelect={handleFileSelect}
        onFileRemove={removeFile}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onPaste={handlePaste}
        isDragOver={isDragOver}
        attachedFiles={attachedFiles}
        className={!showWorkspace ? 'full-width' : ''}
        isStreaming={isStreaming}
        isChatLoading={isChatLoading}
        streamingResponse={streamingResponse}
        showWorkspace={showWorkspace}
        isFollowUpQuery={isCurrentQueryFollowUp}
        projectName={project.name}
        isUploadingFiles={isUploadingFiles}
        uploadingMessageId={uploadingMessageId}
        uploadProgress={uploadProgress}
        hasUserApproved={hasUserApproved}
        isAgentMode={currentProject.mode === 'research'}
      />

      {/* Right Content Panel */}
      {showWorkspace && (
        <WorkspaceContent
          project={project}
          streamResult={isStreaming ? streamingResponse : latestStreamResult}
          isStreaming={isStreaming}
          isFollowUpQuery={isCurrentQueryFollowUp}
          onSyncProject={handleSyncProject}
          isSyncing={isSyncing}
          syncProgress={syncProgress}
        />
      )}

      {/* Rename Project Dialog */}
      <RenameProjectDialog
        isOpen={isRenameDialogOpen}
        onClose={handleRenameCancel}
        onRename={handleRenameConfirm}
        projectName={currentProject.name}
      />

      {/* Error Toast */}
      <Toast
        message={toastMessage}
        isVisible={toastVisible}
        onClose={() => setToastVisible(false)}
        type={toastType}
        duration={6000}
      />
    </main>
  );
};

export default DashboardWorkspaceView;